import { FC, HTMLProps, ChangeEvent } from "react";
import clsx from "clsx";
import { useMediaQuery } from "@mui/material";

import FieldError from "../FieldError";
import styles from "./styles.module.scss";

type SelectValue = string | string[];

export type Props = Omit<
    HTMLProps<HTMLSelectElement>,
    "onChange" | "multiple" | "value" | "defaultValue"
> & {
    onChanged?: (value: SelectValue) => void;
    /**
     * Wrapper class (outer div) to allow custom layouts.
     */
    className?: string;
    /**
     * Custom class applied to the native select for styling overrides.
     */
    selectClassName?: string;
    children?: React.ReactNode;
    defaultValue?: SelectValue;
    disabled?: boolean;
    multiple?: boolean;
    /**
     * Optional variant to toggle extra styling presets.
     */
    variant?: "default" | "custom";
    onChange?: HTMLProps<HTMLSelectElement>["onChange"];
    value?: SelectValue;
    /**
     * When the viewport is mobile (max-width: 1050px), applied as aria-label and title
     * so the field remains understandable without a visible label.
     */
    mobileAriaLabel?: string;
    error?: string;
    /** Bordure rouge sans message (ex. 2e select d’un MonthPicker). */
    showErrorMessage?: boolean;
};

export const Select: FC<Props> = ({
    value,
    onChanged,
    className,
    selectClassName,
    children,
    defaultValue,
    disabled,
    multiple = false,
    variant = "default",
    onChange,
    mobileAriaLabel,
    error,
    showErrorMessage = true,
    ...props
}) => {
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const mobileAccessibility =
        isMobile && mobileAriaLabel
            ? { "aria-label": mobileAriaLabel, title: mobileAriaLabel }
            : {};
    const normalizedValue = multiple
        ? Array.isArray(value)
            ? value
            : value !== undefined
            ? [value]
            : undefined
        : Array.isArray(value)
        ? value[0] ?? ""
        : value;

    const normalizedDefaultValue = multiple
        ? Array.isArray(defaultValue)
            ? defaultValue
            : defaultValue !== undefined
            ? [defaultValue]
            : undefined
        : Array.isArray(defaultValue)
        ? defaultValue[0] ?? ""
        : defaultValue;

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
        if (multiple) {
            const selected = Array.from(e.currentTarget.selectedOptions).map(
                (option) => option.value
            );
            onChanged?.(selected);
        } else {
            onChanged?.(e.currentTarget.value);
        }

        onChange?.(e);
    };

    return (
        <div className={clsx(styles.wrapper, className)}>
            <select
                {...props}
                {...mobileAccessibility}
                multiple={multiple}
                value={normalizedValue as any}
                defaultValue={
                    normalizedValue === undefined ? normalizedDefaultValue : undefined
                }
                onChange={handleChange}
                className={clsx(
                    styles.select,
                    variant === "custom" && styles.selectCustom,
                    error && styles.selectError,
                    selectClassName
                )}
                aria-invalid={Boolean(error)}
                disabled={disabled}
            >
                {children}
            </select>
            {showErrorMessage && <FieldError error={error} />}
        </div>
    );
};
